import { toast } from 'sonner';
import i18next from 'i18next';
import { useReportArchiveStore } from '@/store/report-archive.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ReportArchiveIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchAll() {
        const store = useReportArchiveStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        const { searchParams } = store;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('reportArchive:messages.tenantRequired', {
                    defaultValue: 'Tenant selection is required',
                }),
            };
        }

        try {
            const response = await this.service.getAll({
                page: searchParams.page,
                pageSize: searchParams.pageSize,
                search: searchParams.search,
                sortBy: searchParams.sortBy,
                sortOrder: searchParams.sortOrder,
                tenantId,
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    page: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    total: response.pagination.total,
                    pageSize: response.pagination.pageSize,
                });
            }

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async getViewerAccess(id) {
        if (!id) {
            return {
                success: false,
                error: i18next.t('reportArchive:messages.recordIdRequired', {
                    defaultValue: 'Record id required',
                }),
            };
        }

        try {
            const response = await this.service.getViewerAccess(id);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async delete(id) {
        const store = useReportArchiveStore.getState();
        if (!id) {
            return {
                success: false,
                error: i18next.t('reportArchive:messages.recordIdRequired', {
                    defaultValue: 'Record id required',
                }),
            };
        }

        store.setLoading(true);
        store.clearError();

        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('reportArchive:messages.deleteSuccess', {
                    defaultValue: 'Archive record deleted',
                }),
            );
            await this.fetchAll();
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
